import isNil from 'lodash-es/isNil';
import { CrossConnectServiceData } from 'services/Colocation/CrossConnect/crossConnectServiceTypes';
import {
  CANCEL_CROSS_CONNECT_IS_COMPLETE,
  CANCEL_CROSS_CONNECT_IS_ERROR,
  CANCEL_CROSS_CONNECT_IS_PENDING,
  CROSS_CONNECT_SORT,
  CrossConnectActions,
  CrossConnectReducerState,
  EDIT_CROSS_CONNECT_BY_ID_IS_COMPLETE,
  EDIT_CROSS_CONNECT_BY_ID_IS_ERROR,
  EDIT_CROSS_CONNECT_BY_ID_IS_PENDING,
  FETCH_CROSS_CONNECT_BY_ID_IS_COMPLETE,
  FETCH_CROSS_CONNECT_BY_ID_IS_ERROR,
  FETCH_CROSS_CONNECT_BY_ID_IS_PENDING,
  FETCH_CROSS_CONNECT_INVOICES_IS_COMPLETE,
  FETCH_CROSS_CONNECT_INVOICES_IS_ERROR,
  FETCH_CROSS_CONNECT_INVOICES_IS_PENDING,
  FETCH_CROSS_CONNECT_IS_COMPLETE,
  FETCH_CROSS_CONNECT_IS_ERROR,
  FETCH_CROSS_CONNECT_IS_PENDING,
  REVERT_CANCEL_CROSS_CONNECT_IS_COMPLETE,
  REVERT_CANCEL_CROSS_CONNECT_IS_ERROR,
  REVERT_CANCEL_CROSS_CONNECT_IS_PENDING,
} from 'services/Colocation/CrossConnect/types';
import dateIsAfter from 'utils/date/dateIsAfter';
import { sort } from 'utils/functions';

const INITIAL_STATE = {
  crossConnect: [],
  details: undefined,
  sortBy: 'connectivityType',
  sortDirection: 'ASC',
  params: {
    type: 'function',
    sortSearchParam: 'connectivityType',
    sortSearchValue: (item: CrossConnectServiceData) => item.connectivityType.split(': ')[1],
  },
  fetchCrossConnectIsCompleted: false,
  fetchCrossConnectIsPending: false,
  fetchCrossConnectIsError: null,
  fetchCrossConnectDetailsIsCompleted: false,
  fetchCrossConnectDetailsIsPending: false,
  fetchCrossConnectDetailsIsError: null,
  updateCrossConnectDetailsIsCompleted: false,
  updateCrossConnectDetailsIsPending: false,
  updateCrossConnectDetailsIsError: null,
  fetchInvoicesIsCompleted: false,
  fetchInvoicesIsPending: false,
  fetchInvoicesIsError: null,

  cancelIsCompleted: false,
  cancelIsPending: false,
  cancelIsError: null,

  revertCancelIsCompleted: false,
  revertCancelIsPending: false,
  revertCancelIsError: null,
} satisfies CrossConnectReducerState;

const crossConnectReducer = (previousState: CrossConnectReducerState | undefined, action: CrossConnectActions): CrossConnectReducerState => {
  const state: CrossConnectReducerState = previousState ?? INITIAL_STATE;

  switch (action.type) {
    case CROSS_CONNECT_SORT:
      return {
        ...state,
        sortBy: action.params.sortSearchParam,
        sortDirection: state.sortDirection === 'ASC' ? 'DESC' : 'ASC',
        params: action.params,
        crossConnect: sort(state.crossConnect, {
          sortBy: action.params.sortSearchParam,
          sortDirection: state.sortDirection === 'ASC' ? 'DESC' : 'ASC',
          sortSearchValue: action.params.sortSearchValue || action.params.value,
        }),
      };

    case FETCH_CROSS_CONNECT_IS_COMPLETE: {
      if (isNil(action.data)) {
        return state;
      }

      const isConnectivityActive = ({ dateEndContract, dateEnd }: CrossConnectServiceData) => {
        const currentDate = new Date();
        const hasPassedContractEnd = dateIsAfter(dateEndContract, currentDate, 'day');
        const hasPassedInvoiceEnd = dateIsAfter(dateEnd, currentDate, 'day');

        // show connectivity if either contract or invoice end date hasn't passed
        return !(hasPassedContractEnd && hasPassedInvoiceEnd);
      };

      const activeConnectivities = action.data.filter(isConnectivityActive);

      const sortedConnectivities = sort(activeConnectivities, {
        sortBy: state.sortBy,
        sortDirection: state.sortDirection,
        sortSearchValue: state.params?.sortSearchValue ?? state.params?.value,
      });

      return {
        ...state,
        crossConnect: sortedConnectivities,
        fetchCrossConnectIsCompleted: true,
        fetchCrossConnectIsPending: false,
        fetchCrossConnectIsError: null,
      };
    }

    case FETCH_CROSS_CONNECT_IS_PENDING:
      return {
        ...state,
        fetchCrossConnectIsCompleted: false,
        fetchCrossConnectIsPending: true,
        fetchCrossConnectIsError: null,
      };
    case FETCH_CROSS_CONNECT_IS_ERROR:
      return {
        ...state,
        fetchCrossConnectIsCompleted: true,
        fetchCrossConnectIsPending: false,

        fetchCrossConnectIsError: action.error,
      };

    case FETCH_CROSS_CONNECT_BY_ID_IS_COMPLETE:
      return {
        ...state,

        details: action.data,
        fetchCrossConnectDetailsIsCompleted: true,
        fetchCrossConnectDetailsIsPending: false,
        fetchCrossConnectDetailsIsError: null,
      };
    case FETCH_CROSS_CONNECT_BY_ID_IS_PENDING:
      return {
        ...state,
        fetchCrossConnectDetailsIsCompleted: false,
        fetchCrossConnectDetailsIsPending: true,
        fetchCrossConnectDetailsIsError: null,
      };
    case FETCH_CROSS_CONNECT_BY_ID_IS_ERROR:
      return {
        ...state,

        fetchCrossConnectDetailsIsCompleted: true,
        fetchCrossConnectDetailsIsPending: false,
        fetchCrossConnectDetailsIsError: action.error,
      };

    case EDIT_CROSS_CONNECT_BY_ID_IS_COMPLETE:
      return {
        ...state,
        details: action.data,
        updateCrossConnectDetailsIsCompleted: true,
        updateCrossConnectDetailsIsPending: false,
        updateCrossConnectDetailsIsError: null,
      };
    case EDIT_CROSS_CONNECT_BY_ID_IS_PENDING:
      return {
        ...state,
        updateCrossConnectDetailsIsCompleted: false,
        updateCrossConnectDetailsIsPending: true,
        updateCrossConnectDetailsIsError: null,
      };
    case EDIT_CROSS_CONNECT_BY_ID_IS_ERROR:
      return {
        ...state,
        updateCrossConnectDetailsIsCompleted: true,
        updateCrossConnectDetailsIsPending: false,

        updateCrossConnectDetailsIsError: action.error,
      };

    // "change action": simply toggles state and doesn't use data
    case FETCH_CROSS_CONNECT_INVOICES_IS_COMPLETE:
      return {
        ...state,
        fetchInvoicesIsCompleted: true,
        fetchInvoicesIsPending: false,
        fetchInvoicesIsError: null,
      };
    case FETCH_CROSS_CONNECT_INVOICES_IS_PENDING:
      return {
        ...state,
        fetchInvoicesIsCompleted: false,
        fetchInvoicesIsPending: true,
        fetchInvoicesIsError: null,
      };
    case FETCH_CROSS_CONNECT_INVOICES_IS_ERROR:
      return {
        ...state,
        fetchInvoicesIsCompleted: true,
        fetchInvoicesIsPending: false,
        fetchInvoicesIsError: action.error,
      };

    case CANCEL_CROSS_CONNECT_IS_COMPLETE:
      return {
        ...state,
        cancelIsCompleted: true,
        cancelIsPending: false,
        cancelIsError: null,
      };
    case CANCEL_CROSS_CONNECT_IS_PENDING:
      return {
        ...state,
        cancelIsCompleted: false,
        cancelIsPending: true,
        cancelIsError: null,
      };
    case CANCEL_CROSS_CONNECT_IS_ERROR:
      return {
        ...state,
        cancelIsCompleted: true,
        cancelIsPending: false,
        cancelIsError: action.error,
      };

    case REVERT_CANCEL_CROSS_CONNECT_IS_COMPLETE:
      return {
        ...state,
        revertCancelIsCompleted: true,
        revertCancelIsPending: false,
        revertCancelIsError: null,
      };
    case REVERT_CANCEL_CROSS_CONNECT_IS_PENDING:
      return {
        ...state,
        revertCancelIsCompleted: false,
        revertCancelIsPending: true,
        revertCancelIsError: null,
      };
    case REVERT_CANCEL_CROSS_CONNECT_IS_ERROR:
      return {
        ...state,
        revertCancelIsCompleted: true,
        revertCancelIsPending: false,
        revertCancelIsError: action.error,
      };

    case 'RESET_CROSS_CONNECT_REDUCER':
    case 'RESET_REDUCER':
      return INITIAL_STATE;

    default:
      return state;
  }
};

export default crossConnectReducer;
